/**
 * A modal dialog on the native `<dialog>` element: `showModal()` puts it in the top layer with a backdrop, makes the page
 * behind it inert and keeps the focus inside, without a portal or a focus trap of our own. It is open for as long as it
 * is mounted: the caller closes it by no longer rendering it (`onRequestClose` asks for that).
 *
 * Like every modal it is rendered in a `<ShortcutLayer>` (see `hooks/use-shortcuts.tsx`), by whoever renders it: Esc is a
 * shortcut on that layer, the dialog's own keys (F1, digits, ...) go there as well, and the page's keys are blocked.
 *
 *     {showManco && (
 *         <ShortcutLayer swallowPrintable>
 *             <Dialog
 *                 title={t('manco.title')}
 *                 onRequestClose={() => setShowManco(false)}
 *                 footer={<button type="button" className="button button-primary">...</button>}
 *             >
 *                 <p>...</p>
 *             </Dialog>
 *         </ShortcutLayer>
 *     )}
 *
 * While `busy` (a request of the dialog is underway) it waits for the outcome: a layer on top of its own swallows its
 * keys, Esc and a barcode scan (also in its fields), and the close button is gone. A request that cannot be called back must not seem to be
 * cancelled by closing the dialog, nor its outcome be missed.
 *
 * The content goes straight into the `<dialog>`: no wrappers needed, `.dialog` spaces the header, the content and the
 * footer (see `assets/scss/base.scss`).
 */
import {useEffect, useId, useRef} from 'react';

import {CloseIcon} from '../partials/icons';
import {ShortcutLayer, useShortcuts} from '../../../hooks/use-shortcuts';
import {useTranslation} from '../../../hooks/use-translation';

interface props {
    title: React.ReactNode;
    /** Esc and the close button ask for this; without it the dialog has neither and only closes from within */
    onRequestClose?(): void;
    /** the buttons at the bottom, the main action last */
    footer?: React.ReactNode;
    /**
     * Gets the focus when the dialog opens, e.g. its first input. By default the dialog itself has it: the browser would
     * pick the close button, which the Enter that ends a barcode scan would click.
     */
    initialFocus?: React.RefObject<HTMLElement | null>;
    /** a request is underway: no key, scan or close until its outcome is there (see above) */
    busy?: boolean;
    /** e.g. a wider dialog */
    className?: string;
    children?: React.ReactNode;
}

export const Dialog = ({
    title,
    onRequestClose,
    footer,
    initialFocus,
    busy = false,
    className,
    children,
}: props): React.JSX.Element => {
    const t = useTranslation();
    const titleId = useId();
    const ref = useRef<HTMLDialogElement>(null);

    // also without `onRequestClose`: a handled key prevents its default, so Esc does not close the dialog natively. Not a
    // held Esc's repeat: held while busy, it would close the dialog as soon as the outcome is there, and that with it.
    useShortcuts({
        Escape: event => {
            if (!event.repeat) {
                onRequestClose?.();
            }
        },
    });

    // while busy no printable key does anything, wherever the focus is: the layer on top leaves those typed into a field
    // (a select too) to it, and the barcode scanner reads those of a select. Preventing the keydown loses its keypress.
    // Nor does Esc, which no shortcut handles then: the browser would cancel the `<dialog>`, and its second cancel cannot
    // be prevented any more (see `onCancel` below).
    useEffect(() => {
        if (!busy) {
            return;
        }

        const swallow = (event: KeyboardEvent): void => {
            if (
                'Escape' === event.key ||
                (1 === event.key.length && !event.ctrlKey && !event.altKey && !event.metaKey)
            ) {
                event.preventDefault();
            }
        };

        window.addEventListener('keydown', swallow, true);

        return () => window.removeEventListener('keydown', swallow, true);
    }, [busy]);

    // (StrictMode's remount in development closes and opens it again)
    useEffect(() => {
        const dialog = ref.current!;

        dialog.showModal();
        (initialFocus?.current ?? dialog).focus();

        return () => dialog.close();
    }, [initialFocus]);

    return (
        <dialog
            ref={ref}
            className={className ? `dialog ${className}` : 'dialog'}
            aria-labelledby={titleId}
            tabIndex={-1}
            // Esc is the shortcut layer's (see above): the browser must not close it behind React's back
            onCancel={event => event.preventDefault()}
        >
            <header>
                <h2 id={titleId}>{title}</h2>
                {onRequestClose && !busy && (
                    <button
                        type="button"
                        className="button button-quiet button-small"
                        aria-label={t('common.close')}
                        onClick={onRequestClose}
                    >
                        <kbd>Esc</kbd>
                        <CloseIcon />
                    </button>
                )}
            </header>
            {children}
            {busy && (
                <ShortcutLayer swallowPrintable>
                    <p className="dialog-busy" role="status">
                        {t('common.busy')}
                    </p>
                </ShortcutLayer>
            )}
            {footer && <footer>{footer}</footer>}
        </dialog>
    );
};
